<template>
  <div v-if="showSettingsModal" class="sheet-scrim" @click.self="onScrimClick">
    <div class="sheet settings-sheet" role="dialog" aria-modal="true" aria-labelledby="settingsTitle">
      <div class="sheet-head">
        <h2 id="settingsTitle" class="sheet-title">{{ firstRun ? 'Set up your languages' : 'Settings' }}</h2>
        <button v-if="!firstRun" type="button" class="icon-btn icon-btn-filled icon-btn-sm" aria-label="Close settings" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="sheet-body">
        <p v-if="firstRun" class="first-run-subtitle">Choose the language you want to practice and your own language for subtitles.</p>

        <div v-if="!firstRun" class="segmented settings-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'drill'"
            :class="{ 'is-active': activeTab === 'drill' }"
            @click="setTab('drill')"
          >Practice</button>
          <button
            type="button"
            role="tab"
            :aria-selected="activeTab === 'account'"
            :class="{ 'is-active': activeTab === 'account' }"
            @click="setTab('account')"
          >Account</button>
        </div>

        <div v-show="firstRun || activeTab === 'drill'" class="tab-panel">
          <div v-if="!firstRun" class="setting-item">
            <span class="field-label">Suggested videos</span>
            <div class="video-picker">
              <button
                v-for="video in curatedVideos"
                :key="video.videoId"
                type="button"
                class="video-chip"
                :class="{ 'video-chip--selected': isSelectedVideo(video) }"
                @click="pickVideo(video)"
              >
                <img class="video-chip__thumb" :src="thumbnailUrl(video.videoId)" alt="" loading="lazy">
                <span class="video-chip__body">
                  <span class="video-chip__title">{{ video.title }}</span>
                  <span class="video-chip__meta">{{ video.level }} · {{ video.topic }}</span>
                </span>
              </button>
            </div>
          </div>

          <template v-if="!firstRun">
            <div class="setting-item">
              <label class="field-label" for="videoUrl">YouTube link</label>
              <input id="videoUrl" v-model="formData.videoUrl" class="field" type="text" inputmode="url" placeholder="Paste a YouTube link" @input="onVideoUrlInput">
            </div>
            <div class="setting-item">
              <span class="field-label">Start at</span>
              <div class="time-input-container">
                <label class="time-input-group">
                  <input v-model.number="formData.startTimeHours" class="field" type="number" min="0" step="1" inputmode="numeric" aria-label="Hours">
                  <span class="time-label">h</span>
                </label>
                <label class="time-input-group">
                  <input v-model.number="formData.startTimeMinutes" class="field" type="number" min="0" max="59" step="1" inputmode="numeric" aria-label="Minutes">
                  <span class="time-label">m</span>
                </label>
                <label class="time-input-group">
                  <input v-model.number="formData.startTimeSeconds" class="field" type="number" min="0" max="59" step="1" inputmode="numeric" aria-label="Seconds">
                  <span class="time-label">s</span>
                </label>
              </div>
            </div>
          </template>

          <div class="setting-row">
            <div class="setting-item">
              <label class="field-label" for="targetLanguage">I'm learning</label>
              <select id="targetLanguage" v-model="formData.targetLanguage" class="field">
                <option v-for="lang in availableLanguages" :key="lang.code" :value="lang">{{ lang.name }}</option>
              </select>
            </div>

            <div class="setting-item">
              <label class="field-label" for="sourceLanguage">I speak</label>
              <select id="sourceLanguage" v-model="formData.sourceLanguage" class="field">
                <option v-for="lang in availableLanguages" :key="lang.code" :value="lang">{{ lang.name }}</option>
              </select>
            </div>
          </div>

          <div v-if="!firstRun" class="setting-item">
            <span class="field-label">Speech recognition</span>
            <div class="segmented">
              <button
                v-for="engine in asrEngines"
                :key="engine.value"
                type="button"
                :class="{ 'is-active': formData.asrEngine === engine.value }"
                @click="selectEngine(engine.value)"
              >{{ engine.label }}</button>
            </div>
            <p v-if="formData.asrEngine === 'moonshine'" class="engine-note">
              The on-device model downloads about 63 MB once, then runs entirely on your device.
            </p>
            <div v-if="formData.asrEngine === 'moonshine'" class="engine-progress">
              <div class="engine-progress__track">
                <div
                  class="engine-progress__fill"
                  :class="'engine-progress__fill--' + moonshineState.status"
                  :style="{ width: (moonshineState.status === 'ready' ? 100 : moonshineProgressPct) + '%' }"
                ></div>
              </div>
              <p class="engine-progress__status" :class="{ 'engine-progress__status--error': moonshineState.status === 'error' }">
                <i class="fas" :class="moonshineStatusIcon"></i> {{ moonshineStatusText }}
              </p>
              <button v-if="moonshineState.status === 'error'" type="button" class="btn btn-secondary btn-sm" @click="retryMoonshineDownload">Retry</button>
            </div>
          </div>
        </div>

        <div v-if="!firstRun && activeTab === 'account'" class="tab-panel">
          <account-panel />
        </div>
      </div>

      <div v-show="firstRun || activeTab === 'drill'" class="sheet-foot">
        <button type="button" class="btn btn-primary btn-block" @click="saveSettings">{{ firstRun ? "Let's go" : 'Save' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { getCuratedVideos } from '../../services/videoinfo';
import { ASR_ENGINES } from '../../store/modules/settings';
import AccountPanel from './AccountPanel.vue';
import {
  preload as preloadMoonshine,
  retry as retryMoonshine,
  getModelState as getMoonshineState,
  subscribeProgress as subscribeMoonshineProgress,
  unsubscribeProgress as unsubscribeMoonshineProgress
} from '../../services/asrMoonshine';

export default {
  name: 'SettingsModal',

  components: { AccountPanel },

  props: {
    firstRun: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      formData: {
        videoUrl: '',
        targetLanguage: null,
        sourceLanguage: null,
        startTimeHours: 0,
        startTimeMinutes: 0,
        startTimeSeconds: 0,
        asrEngine: 'worker'
      },
      curatedVideos: getCuratedVideos(),
      asrEngines: ASR_ENGINES,
      isInitialized: false,
      moonshineState: getMoonshineState()
    }
  },

  computed: {
    // Store-owned so callers can deep-link (Stats opens straight onto Account).
    activeTab() {
      return this.$store.state.settings.settingsTab;
    },
    ...mapState({
      showSettingsModal: state => state.settings.showSettingsModal,
      availableLanguages: state => state.settings.availableLanguages,
      storeTargetLanguage: state => state.settings.targetLanguage,
      storeSourceLanguage: state => state.settings.sourceLanguage,
      storeAsrEngine: state => state.settings.asrEngine,
      videoId: state => state.player.videoId,
      startTime: state => state.player.startTime
    }),
    moonshineProgressPct() {
      if (!this.moonshineState.total) return 0;
      return Math.min(100, Math.round((this.moonshineState.loaded / this.moonshineState.total) * 100));
    },
    moonshineStatusIcon() {
      switch (this.moonshineState.status) {
        case 'ready': return 'fa-check-circle';
        case 'downloading': return 'fa-circle-notch fa-spin';
        case 'error': return 'fa-exclamation-triangle';
        default: return 'fa-circle';
      }
    },
    moonshineStatusText() {
      var s = this.moonshineState;
      if (s.status === 'ready') return s.cached ? 'Ready (already on this device)' : 'Ready';
      if (s.status === 'downloading') {
        if (!s.total) return 'Preparing…';
        return 'Downloading… ' + this.moonshineProgressPct + '% (' + this.formatMB(s.loaded) + ' / ' + this.formatMB(s.total) + ' MB)';
      }
      if (s.status === 'error') return "Couldn't load the model" + (s.error ? ': ' + s.error : ' - check your connection');
      return 'Not downloaded yet';
    }
  },

  watch: {
    showSettingsModal(newVal) {
      if (newVal) this.initializeFormData();
    }
  },

  mounted() {
    if (this.showSettingsModal) this.initializeFormData();
    this._onMoonshineProgress = (state) => { this.moonshineState = state; };
    subscribeMoonshineProgress(this._onMoonshineProgress);
  },

  beforeDestroy() {
    unsubscribeMoonshineProgress(this._onMoonshineProgress);
  },

  methods: {
    setTab(tab) {
      this.$store.commit('settings/SET_SETTINGS_TAB', tab);
    },

    selectEngine(engine) {
      this.formData.asrEngine = engine;
      if (engine === 'moonshine') preloadMoonshine();
    },

    retryMoonshineDownload() {
      retryMoonshine();
    },

    formatMB(bytes) {
      return (bytes / (1024 * 1024)).toFixed(1);
    },

    thumbnailUrl(videoId) {
      return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
    },

    isSelectedVideo(video) {
      return this.extractVideoId(this.formData.videoUrl) === video.videoId;
    },

    pickVideo(video) {
      this.formData.videoUrl = `https://youtu.be/${video.videoId}`;
      const timeValues = this.secondsToHMS(video.startTime || 0);
      this.formData.startTimeHours = timeValues.hours;
      this.formData.startTimeMinutes = timeValues.minutes;
      this.formData.startTimeSeconds = timeValues.seconds;

      if (window.$analytics) {
        window.$analytics.trackEvent('curated_video_picked', {
          category: 'Video Library',
          label: video.videoId
        });
      }
    },

    extractVideoId(url) {
      if (!url) return '';
      const normalized = url.replace(/#\//, '/');
      const regexPatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/|youtube\.com\/shorts\/|shadow-ez\.com\/watch\?v=|shadow-ez\.pages\.dev\/watch\?v=|shadow-ez\.com\/shorts\/|shadow-ez\.pages\.dev\/shorts\/|localhost(?::\d+)?\/watch\?v=|localhost(?::\d+)?\/shorts\/)([^&?/]+)/,
        /(?:youtube\.com\/watch.*v=|shadow-ez\.com\/[^#]*[?&]v=|shadow-ez\.pages\.dev\/[^#]*[?&]v=)([^&]+)/,
        /[?&]v=([^&?#]+)/
      ];
      for (const regex of regexPatterns) {
        const match = normalized.match(regex);
        if (match && match[1]) return match[1];
      }
      return url;
    },

    extractStartTime(url) {
      if (!url) return null;
      const m = url.match(/[?&]t=(\d+)/);
      if (m) return parseInt(m[1], 10);
      return null;
    },

    onVideoUrlInput(e) {
      const st = this.extractStartTime(e.target.value);
      if (st !== null) {
        const hms = this.secondsToHMS(st);
        this.formData.startTimeHours = hms.hours;
        this.formData.startTimeMinutes = hms.minutes;
        this.formData.startTimeSeconds = hms.seconds;
        return;
      }
      // No ?t=: reset the start time, or the old video's playback position
      // carries over to the new clip.
      const newId = this.extractVideoId(e.target.value);
      if (newId && newId !== this.videoId) {
        this.formData.startTimeHours = 0;
        this.formData.startTimeMinutes = 0;
        this.formData.startTimeSeconds = 0;
      }
    },

    secondsToHMS(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return { hours, minutes, seconds: secs };
    },

    hmsToSeconds() {
      return (
        (this.formData.startTimeHours || 0) * 3600 +
        (this.formData.startTimeMinutes || 0) * 60 +
        (this.formData.startTimeSeconds || 0)
      );
    },

    initializeFormData() {
      const timeValues = this.secondsToHMS(this.startTime || 0);
      this.formData = {
        videoUrl: this.videoId ? (this.videoId.startsWith('http') ? this.videoId : `https://youtu.be/${this.videoId}`) : '',
        targetLanguage: JSON.parse(JSON.stringify(this.storeTargetLanguage)),
        sourceLanguage: JSON.parse(JSON.stringify(this.storeSourceLanguage)),
        startTimeHours: timeValues.hours,
        startTimeMinutes: timeValues.minutes,
        startTimeSeconds: timeValues.seconds,
        asrEngine: this.storeAsrEngine
      };
      this.isInitialized = true;
      if (this.formData.asrEngine === 'moonshine' && this.moonshineState.status === 'idle') {
        preloadMoonshine();
      }
    },

    saveSettings() {
      const videoId = this.extractVideoId(this.formData.videoUrl);
      if (videoId !== this.videoId) {
        this.$store.dispatch('settings/updateVideoId', videoId);
      }
      // Only the first-run save is a funnel step.
      if (this.firstRun && window.$analytics) {
        window.$analytics.trackEvent('first_run_langs_saved', {
          category: 'Onboarding',
          extraParams: {
            target: (this.formData.targetLanguage || {}).code,
            source: (this.formData.sourceLanguage || {}).code
          }
        });
      }
      const startTimeInSeconds = this.hmsToSeconds();
      this.$store.dispatch('player/setStartTime', startTimeInSeconds);
      this.$store.dispatch('settings/setAsrEngine', this.formData.asrEngine);
      this.$store.dispatch('settings/updateLanguages', {
        targetLanguage: this.formData.targetLanguage,
        sourceLanguage: this.formData.sourceLanguage
      })
      .then(() => {
        return this.$store.dispatch('settings/saveSettings');
      })
      .then(() => {
        this.closeModal();
      })
      .catch(error => {
        console.error('Error saving settings:', error);
      });
    },

    // First run has no close affordance on purpose: languages must be chosen.
    onScrimClick() {
      if (!this.firstRun) this.closeModal();
    },

    closeModal() {
      this.$store.dispatch('settings/closeModal');
    }
  }
}
</script>

<style scoped>
.settings-sheet {
  max-width: 480px;
}

.first-run-subtitle {
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--muted);
}

.settings-tabs {
  margin-bottom: 20px;
}

.tab-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.setting-item {
  min-width: 0;
}

.setting-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.video-picker {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 3px 3px 6px;
  margin: -3px;
  scroll-snap-type: x mandatory;
  /* Snap to the padding edge, or the selected chip's outline is scrolled under it. */
  scroll-padding-inline: 3px;
  scrollbar-width: none;
}
.video-picker::-webkit-scrollbar { display: none; }

.video-chip {
  flex: none;
  width: 148px;
  display: flex;
  flex-direction: column;
  text-align: left;
  border-radius: var(--r-control);
  background: var(--surface-2);
  overflow: hidden;
  scroll-snap-align: start;
  outline: 2px solid transparent;
  outline-offset: 0;
  transition: outline-color 0.15s;
}

.video-chip--selected {
  outline-color: var(--accent);
}

.video-chip__thumb {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
  background: var(--field);
}

.video-chip__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px 10px;
}

.video-chip__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-chip__meta {
  font-size: 11px;
  color: var(--muted);
}

.time-input-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.time-input-group {
  position: relative;
  display: block;
}

.time-input-group .field {
  padding-right: 28px;
  -moz-appearance: textfield;
  font-variant-numeric: tabular-nums;
}
.time-input-group .field::-webkit-outer-spin-button,
.time-input-group .field::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.time-label {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  color: var(--faint);
  pointer-events: none;
}

.engine-note {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--muted);
}

.engine-progress {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.engine-progress__track {
  width: 100%;
  height: 4px;
  border-radius: var(--r-pill);
  background: var(--field);
  overflow: hidden;
}

.engine-progress__fill {
  height: 100%;
  background: var(--accent);
  transition: width 0.3s ease;
}

.engine-progress__fill--ready { background: var(--pass); }
.engine-progress__fill--error { background: var(--fail); }

.engine-progress__status {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
}

.engine-progress__status--error { color: var(--fail); }

@media (max-width: 360px) {
  .setting-row { grid-template-columns: 1fr; }
}
</style>
